import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Pieces shared by the spending page (settings) and the translation spending
// sheet (novels): the period chips, money, and bar lists.

export const PERIODS = [
  { key: 'day', label: '24 hours' },
  { key: 'week', label: '7 days' },
  { key: 'month', label: '30 days' },
  { key: 'all', label: 'all time' },
];

export const money = (n) => {
  const v = Number(n || 0);
  if (v === 0) return '$0';
  if (v < 0.01) return '<$0.01';
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export function PeriodChips({ value, onChange }) {
  return (
    <div style={p.chips} role="tablist" aria-label="period">
      {PERIODS.map(x => (
        <button key={x.key} role="tab" aria-selected={value === x.key} className="sp-btn" style={p.chip(value === x.key)} onClick={() => onChange(x.key)}>
          {x.label}
        </button>
      ))}
    </div>
  );
}

// the four periods side by side, the chosen one lit
export function Totals({ total, period }) {
  return (
    <div style={p.totals}>
      {PERIODS.map(x => (
        <div key={x.key} style={p.totalCell(period === x.key)}>
          <div style={p.totalNum(period === x.key)}>{money(total?.[x.key])}</div>
          <div style={p.totalLabel}>{x.key === 'all' ? 'all time' : `last ${x.label}`}</div>
        </div>
      ))}
    </div>
  );
}

// rows: [{ key, label, sub?, [period]: number }]
export function Bars({ rows, period, empty = 'nothing yet' }) {
  const shown = (rows || []).filter(r => (r[period] || 0) > 0).sort((a, b) => b[period] - a[period]);
  const max = Math.max(...shown.map(r => r[period]), 0.000001);
  if (!shown.length) return <div style={p.empty}>{empty}</div>;
  return (
    <div style={p.bars}>
      {shown.map(r => (
        <div key={r.key} style={p.barRow}>
          <div style={p.barTop}>
            <span style={p.barLabel}>{r.label}{r.sub && <span style={p.barSub}> · {r.sub}</span>}</span>
            <span style={p.barNum}>{money(r[period])}</span>
          </div>
          <div style={p.track}><div style={{ ...p.fill, width: `${Math.max(2, (r[period] / max) * 100)}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export const spendStyle = `
  .sp-btn:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
`;

// The sheet on the novels page: translation only.
export function TranslationSpendingSheet({ onClose }) {
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/spending/translation?token=${token}`)
      .then(res => setData(res.data))
      .catch(() => setErrorMsg("couldn't load it — try again"));
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div style={p.overlay} onClick={onClose}>
      <div style={p.sheet} onClick={e => e.stopPropagation()} role="dialog" aria-label="translation spending">
        <style>{spendStyle}</style>
        <div style={p.head}>
          <div style={p.title}>translation spending</div>
          <button className="sp-btn" style={p.close} onClick={onClose} aria-label="close">×</button>
        </div>
        {errorMsg && <div style={p.error}>{errorMsg}</div>}
        {!data && !errorMsg && <div style={p.empty}>adding it up…</div>}
        {data && (
          <>
            <Totals total={data.total} period={period} />
            <PeriodChips value={period} onChange={setPeriod} />
            <div style={p.label}>by book</div>
            <Bars period={period} empty="nothing translated in this period"
              rows={data.books.map(b => ({ key: b.id || 'none', label: b.title, sub: b.deleted ? 'deleted' : null, ...b }))} />
            <div style={p.label}>by model</div>
            <Bars period={period} empty="nothing in this period" rows={data.models.map(m => ({ key: m.model, ...m }))} />
            <div style={p.label}>by kind of work</div>
            <Bars period={period} empty="nothing in this period" rows={data.kinds} />
            <div style={p.note}>includes pieces that failed or were retried — those were billed too.</div>
          </>
        )}
      </div>
    </div>
  );
}

const serif = "'Cormorant Garamond', serif";
const sans = "'DM Sans', sans-serif";
export const p = {
  chips: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  chip: (on) => ({ padding: '7px 13px', borderRadius: '14px', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans, border: on ? 'none' : '1px solid rgba(201,147,90,0.18)', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#c9a57a' }),
  totals: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '6px' },
  totalCell: (on) => ({ padding: '12px 8px', borderRadius: '14px', background: on ? 'rgba(201,147,90,0.1)' : '#17131b', border: `1px solid ${on ? 'rgba(201,147,90,0.35)' : 'transparent'}`, textAlign: 'center', minWidth: 0 }),
  totalNum: (on) => ({ fontFamily: serif, fontSize: on ? '24px' : '20px', color: on ? '#e8c9a4' : '#c9b8a6', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  totalLabel: { fontSize: '10.5px', color: '#7a6a5a', marginTop: '4px' },
  bars: { display: 'flex', flexDirection: 'column', gap: '10px' },
  barRow: { display: 'flex', flexDirection: 'column', gap: '5px' },
  barTop: { display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px' },
  barLabel: { color: '#e0d4c6', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
  barSub: { color: '#7a6a5a', fontSize: '11.5px' },
  barNum: { color: '#c9935a', fontVariantNumeric: 'tabular-nums', flexShrink: 0 },
  track: { height: '6px', borderRadius: '3px', background: 'rgba(201,147,90,0.1)', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: '3px', background: 'linear-gradient(90deg, #8a5f35, #c9935a)' },
  empty: { fontSize: '12.5px', color: '#6d5d4e', padding: '4px 0' },
  label: { fontSize: '10px', color: '#8a7466', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '10px' },
  note: { fontSize: '11.5px', color: '#6d5d4e', lineHeight: 1.55, marginTop: '6px' },
  overlay: { position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(8,6,10,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet: { width: '100%', maxWidth: '560px', maxHeight: '88vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '22px 22px 0 0', padding: '18px 18px calc(26px + env(safe-area-inset-bottom))', boxSizing: 'border-box', fontFamily: sans, color: '#e8ddd0', fontWeight: 300 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontFamily: serif, fontSize: '24px', fontStyle: 'italic' },
  close: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '20px', cursor: 'pointer' },
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0' },
};
